import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { useDetachPolicy, useTerminatePolicy } from '../api/policies';
import { promotedPartners } from '../model/partners';
import { ConflictNotice } from './ConflictNotice';
import { PartnerList } from './PartnerList';

/**
 * Detachment — a role promotion, not a field update (service summary §4.1).
 *
 * Six things happen at once, and the dialog says so because five of them are consequences the
 * operator is agreeing to rather than choosing: the parent pointer is cleared, the insured person
 * is promoted to policyholder, a payment method is collected, billing flips to DIRECT, the
 * document set changes, and the policy is flagged for re-pricing because it has lost the group
 * factor.
 *
 * The payment method is the one input, and it is required: a detached policy pays for itself, and
 * billing flips in the same step — "detachment cannot complete without one" is the contract's own
 * wording.
 *
 * The member keeps its tarif pins. It is the same cover, billed differently.
 */
export function DetachDialog({
  code,
  policy,
  rules,
  open,
  onClose,
}: {
  readonly code: string;
  /** The policy as read, so the dialog can show the parties AFTER the promotion (SDD-011 R-7). */
  readonly policy?: InstanceApi.Policy;
  readonly rules?: readonly InstanceApi.PartnerRoleRule[];
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const detach = useDetachPolicy();
  // The list detachment writes: the insured person fills the policyholder role, the rest stays.
  const promoted = promotedPartners(policy).map((link) => ({
    link,
    partnerName:
      policy?.partners?.find((p) => p.link?.partnerId === link.partnerId)?.partnerName ?? '',
    partnerNameResolved:
      policy?.partners?.find((p) => p.link?.partnerId === link.partnerId)?.partnerNameResolved ??
      false,
    partnerTypeChecked:
      policy?.partners?.find((p) => p.link?.partnerId === link.partnerId)?.partnerTypeChecked ??
      false,
  }));
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [paymentMethodRef, setPaymentMethodRef] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const missing = {
    effectiveFrom: effectiveFrom === '',
    paymentMethodRef: paymentMethodRef === '',
  };

  const close = (): void => {
    setSubmitted(false);
    setEffectiveFrom('');
    setPaymentMethodRef('');
    detach.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t('policies.detach.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning">
            <AlertTitle>{t('policies.detach.consequences')}</AlertTitle>
            <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
              <Typography component="li" variant="body2">
                {t('policies.detach.promotion')}
              </Typography>
              <Typography component="li" variant="body2">
                {t('policies.detach.billing')}
              </Typography>
              <Typography component="li" variant="body2">
                {t('policies.detach.documents')}
              </Typography>
              {/* The one an operator will be asked about afterwards: losing the group factor
                  changes what the customer pays. */}
              <Typography component="li" variant="body2" data-testid="repricing-notice">
                {t('policies.detach.repricing')}
              </Typography>
            </Stack>
          </Alert>

          <ConflictNotice conflict={detach.conflict} />

          {policy !== undefined && (
            <Stack spacing={0.5} component="section" aria-label={t('policies.partners.title')}>
              <Typography variant="body2" color="text.secondary">
                {t('policies.partners.promotion')}
              </Typography>
              <PartnerList partners={promoted} rules={rules} />
            </Stack>
          )}

          <TextField
            type="date"
            label={t('policies.detach.effectiveFrom')}
            value={effectiveFrom}
            required
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(event) => {
              setEffectiveFrom(event.target.value);
            }}
            error={submitted && missing.effectiveFrom}
            helperText={
              submitted && missing.effectiveFrom ? t('policies.endorse.errors.required') : undefined
            }
          />

          <TextField
            label={t('policies.detach.paymentMethod')}
            value={paymentMethodRef}
            required
            fullWidth
            onChange={(event) => {
              setPaymentMethodRef(event.target.value);
            }}
            error={submitted && missing.paymentMethodRef}
            helperText={
              submitted && missing.paymentMethodRef
                ? t('policies.endorse.errors.required')
                : t('policies.detach.paymentMethodHint')
            }
          />

          {detach.isSuccess && <Alert severity="success">{t('policies.detach.done')}</Alert>}
          {detach.isError && detach.conflict === null && (
            <Alert severity="error">{detach.error.message}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{t('policies.actions.cancel')}</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={detach.isPending || detach.isSuccess}
          onClick={() => {
            setSubmitted(true);
            if (missing.effectiveFrom || missing.paymentMethodRef) return;
            detach.mutate({ code, effectiveFrom, paymentMethodRef });
          }}
        >
          {t('policies.detach.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Termination — dated, never "now".
 *
 * Cover ends on a business day, and which day decides what a claim on the 14th is worth. At the
 * storage layer this is an endorsement with a different payload, which is why it moves the head
 * and carries the same precondition as any other write.
 */
export function TerminateDialog({
  code,
  open,
  onClose,
}: {
  readonly code: string;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const terminate = useTerminatePolicy();
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const close = (): void => {
    setSubmitted(false);
    setEffectiveFrom('');
    setReason('');
    terminate.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t('policies.terminate.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning">{t('policies.terminate.description')}</Alert>
          <ConflictNotice conflict={terminate.conflict} />

          <TextField
            type="date"
            label={t('policies.terminate.effectiveFrom')}
            value={effectiveFrom}
            required
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(event) => {
              setEffectiveFrom(event.target.value);
            }}
            error={submitted && effectiveFrom === ''}
            helperText={
              submitted && effectiveFrom === ''
                ? t('policies.endorse.errors.required')
                : t('policies.terminate.effectiveFromHint')
            }
          />

          <TextField
            label={t('policies.terminate.reason')}
            value={reason}
            fullWidth
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />

          {terminate.isSuccess && <Alert severity="success">{t('policies.terminate.done')}</Alert>}
          {terminate.isError && terminate.conflict === null && (
            <Alert severity="error">{terminate.error.message}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{t('policies.actions.cancel')}</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={terminate.isPending || terminate.isSuccess}
          onClick={() => {
            setSubmitted(true);
            if (effectiveFrom === '') return;
            terminate.mutate({ code, effectiveFrom, ...(reason === '' ? {} : { reason }) });
          }}
        >
          {t('policies.terminate.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
